import { Component, EventEmitter, OnInit } from '@angular/core';
import { MembershipService } from '../../services/membership.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { MembershipCustomerList } from '../../models/membership-customer-list';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { MembershipCustomerListQueryFilter } from './membership-customer-list-query-filter';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MembershipCustomerCreateModalComponent } from '../../modals/membership/membership-customer-create-modal/membership-customer-create-modal.component';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { MembershipCustomerEditModalComponent } from '../../modals/membership/membership-customer-edit-modal/membership-customer-edit-modal.component';

@Component({
  selector: 'membership-customer-list',
  templateUrl: './membership-customer-list.component.html',
  styleUrl: './membership-customer-list.component.scss',
  standalone: false,
})
export class MembershipCustomerListComponent extends BaseComponent implements OnInit {
  membershipCustomers: MembershipCustomerList[] = [];
  isLoading = false;
  filter: MembershipCustomerListQueryFilter = new MembershipCustomerListQueryFilter();
  public updateFilterEvent = new EventEmitter<any>();
  displayedColumns = [
    'firstName', 'lastName', 'emailAddress', 'discountCode', 'membershipPlanName', 'membershipPlanPrice', 'startDate', 'endDate', 'isCancelled', 'actions'
  ];

  constructor(sessionService: SessionService,
    private membershipService: MembershipService,
    stringHelpers: StringHelpers,
    private dialog: MatDialog) {
    super(sessionService, stringHelpers);
    this.filter.organizationId = this.sessionService.getUser().organizationId;
  }

  ngOnInit(): void {
    this.getMembershipCustomers();
  }

  getMembershipCustomers() {
    this.isLoading = true;
    this.membershipService.getAllCustomersByOrganization(this.filter).subscribe({
      next: (result: any) => {
        this.membershipCustomers = result.results || result;
        this.filter.totalCount = result.totalCount;
        this.updateFilterEvent.emit(result.results);
        this.isLoading = false;
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error(error);
      }
    });
  }

  sortData(sort: Sort) {
    this.filter.sortDirection = sort.direction;
    this.filter.sortColumn = sort.active;
    this.getMembershipCustomers();
  }

  resetFilter() {
    this.filter.page = 1;
    this.filter.text = '';
    this.filter.pageSize = 15;
  }

  addMembershipCustomer() {
    const dialogRef = this.dialog.open(MembershipCustomerCreateModalComponent, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'save') {
        this.getMembershipCustomers();
      }
    });
  }

  editMembershipCustomer(membershipCustomer: MembershipCustomerList) {
    const dialogRef = this.dialog.open(MembershipCustomerEditModalComponent, {
      disableClose: true,
      data: membershipCustomer,
    });

    dialogRef.componentInstance.title = 'Edit Membership';
    dialogRef.componentInstance.isRenewal = false;

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'save') {
        this.getMembershipCustomers();
      }
    });
  }

  renewMembershipCustomer(membershipCustomer: MembershipCustomerList) {
    const dialogRef = this.dialog.open(MembershipCustomerEditModalComponent, {
      disableClose: true,
      data: membershipCustomer,
    });

    dialogRef.componentInstance.title = 'Renew Membership';
    dialogRef.componentInstance.isRenewal = true;

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'save') {
        this.getMembershipCustomers();
      }
    });
  }

  cancelMembershipCustomer(membershipCustomer: MembershipCustomerList) {
    const dialogRef = this.dialog.open(AlertModalComponent);
    dialogRef.componentInstance.title = 'Cancel Customer';
    dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> Once this membership is cancelled it can no longer be activated again. <br /><br /> Are you sure you want to cancel the membership for customer <strong>${membershipCustomer.firstName} ${membershipCustomer.lastName}</strong>?`;
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'No';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      if(response === 'Yes'){
        this.isLoading = true;
        membershipCustomer.isCanceled = true;
        membershipCustomer.canceledAt = this.convertToUTC(new Date());
        membershipCustomer.lastEditedBy = this.sessionService.getUserId();

        this.membershipService.cancelCustomer(membershipCustomer).subscribe({
          next: () => {
            this.getMembershipCustomers();
          },
          error: (error) => {
            this.isLoading = false;
            console.error(error);
          },
          complete: () => this.isLoading = false
        });
      }
    });
  }

  deleteMembershipCustomer(membershipCustomer: MembershipCustomerList) {
    const dialogRef = this.dialog.open(AlertModalComponent);
    dialogRef.componentInstance.title = 'Cancel Customer';
    dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> This action will cancel the customers membership and delete them from your customers list. <br /><br /> Are you sure you want to delete customer <strong>${membershipCustomer.firstName} ${membershipCustomer.lastName}</strong>?`;
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'No';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      if(response === 'Yes'){
        this.isLoading = true;
        let data = {
          id: membershipCustomer.id,
          accountId: this.sessionService.getUser().stripeAccountId,
          stripeCustomerId: membershipCustomer.stripeCustomerId,
          lastEditedBy: this.sessionService.getUserId()
        };
        this.membershipService.deleteCustomer(data).subscribe({
          next: () => {
            this.getMembershipCustomers();
          },
          error: (error) => {
            this.isLoading = false;
            console.error(error);
          },
          complete: () => this.isLoading = false
        });
      }
    });
  }

  private convertToUTC(date: Date): Date {
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    ));
  }
}
